import React, { useState } from "react";
import { SimpleForm, useDataProvider, useNotify, useRefresh, Button } from "react-admin";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import {
  Dialog,
  DialogContent,
  DialogContentText,
  Box,
  Chip,
  Grid,
  MenuItem,
  TextField,
  Typography,
  CircularProgress,
  InputLabel,
} from "@mui/material";
import { StatusExamsChoices } from "utils/choices";
import { accountRequests } from "api/accountApi";
import { studentRequests } from "api/studentApi";
import { modalLoading } from "customStyles/styles/style.mu";
import {
  DISCIPLINES,
  RECORDS,
  SessionExamEnrollmentStatus,
  STUDENT_EDUCATION,
  StudentExamStatus,
  StudentServicePath,
} from "utils/constants";
import { checkPermission } from "server/security/utils";
import { getPermissions } from "auth/security/securityUtils";
import permissions from "app/permissions";
import { DialogActionsButtons } from "server/components/Modals/ConfirmModal";
import ModalTitle, { withModalSx } from "server/components/Modals/ModalTitle";
import SecuredComponent from "server/security/SecuredComponent";
import { useNotifyError } from "utils/useNotifyError";

const GradesModal = ({ item }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState([]);
  const [errors, setErrors] = useState([]);
  const [helperTexts, setHelperTexts] = useState([]);
  const [loading, setLoading] = useState(false);

  const dataProvider = useDataProvider();
  const notify = useNotify();
  const notifyError = useNotifyError();
  const refresh = useRefresh();

  const validateGrade = (index, val) => {
    const num = parseInt(val);

    let error = false;
    let helperText = "";

    if (isNaN(num) || !Number.isInteger(Number(val)) || ![1, 2, 3, 4, 5, 6].includes(num)) {
      error = true;
      helperText = "Оценката трябва да е цяло число между 1 и 6.";
    }

    setErrors(prev => {
      const updated = [...prev];
      updated[index] = error;
      return updated;
    });

    setHelperTexts(prev => {
      const updated = [...prev];
      updated[index] = helperText;
      return updated;
    });
  };

  const handleClickOpen = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const response = await dataProvider.customRequest(studentRequests.getSessionExamStudents(item.id));
      const students = response.data;

      const settedFields = await Promise.all(
        students.map(async itemStudent => {
          const userResponse = itemStudent.userId
            ? await dataProvider.customRequest(accountRequests.getUsersByIdsSelect(itemStudent.userId))
            : null;

          const user = userResponse?.data[0] || null;

          return {
            status: itemStudent.status || StudentExamStatus.Attended,
            studentEducationId: itemStudent.studentEducationId,
            grade: itemStudent?.grade || "",
            points: itemStudent?.points,
            note: itemStudent?.note,
            userId: itemStudent?.userId,
            isRemote: itemStudent?.isRemote,
            enrollmentStatus: itemStudent?.enrollmentStatus,
            studentFacultyNumber: itemStudent?.studentFacultyNumber || "",
            userName: user ? `${user.firstName} ${user.lastName}` : "Няма име",
          };
        }),
      );

      setData(settedFields);
    } catch (error) {
      notifyError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setData([]);
    setErrors([]);
    setHelperTexts([]);
  };

  const handleChange = (index, field, value) => {
    setData(prevData => prevData.map((student, idx) => (idx === index ? { ...student, [field]: value } : student)));
  };

  const handleSave = async () => {
    setLoading(true);

    // NotAdmitted students do not enter the protocol — they are read-only and excluded from grading
    const body = data
      .filter(item => item.enrollmentStatus !== SessionExamEnrollmentStatus.NotAdmitted)
      .map(item =>
        item.isRemote
          ? {
              status: item.status,
              studentEducationId: item.studentEducationId,
              points: item?.points,
            }
          : {
              status: item.status,
              studentEducationId: item.studentEducationId,
              grade: item?.grade,
            },
      );
    try {
      await dataProvider.customRequest(
        studentRequests.updateSessionExamGrades(item.id),
        { studentsGrades: body },
        "PUT",
      );
      notify("Успешно изпратена заявка.", { type: "success" });
      refresh();
      setOpen(false);
    } catch (error) {
      notifyError(error);
    } finally {
      setLoading(false);
    }
  };

  // Handle status change: required + disable grade if status is not Attended  */
  const handleStatusChange = (index, newStatus, isRemote) => {
    handleChange(index, "status", newStatus);
    if (!isRemote) {
      if (newStatus === StudentExamStatus.Disqualified) {
        handleChange(index, "grade", 2);
        validateGrade(index, 2);
      } else if (newStatus === StudentExamStatus.NotAttended) {
        handleChange(index, "grade", "");
        setErrors(prev => {
          const updated = [...prev];
          updated[index] = false;
          return updated;
        });
        setHelperTexts(prev => {
          const updated = [...prev];
          updated[index] = "";
          return updated;
        });
      } else {
        handleChange(index, "grade", "");
        setErrors(prev => {
          const updated = [...prev];
          updated[index] = true;
          return updated;
        });
        setHelperTexts(prev => {
          const updated = [...prev];
          updated[index] = "Моля, въведете оценка.";
          return updated;
        });
      }
    }
  };

  const isSaveDisabled = () => {
    const hasPermission = checkPermission(getPermissions(), permissions.canUpdateSessionExamGrades);

    const gradableStudents = data?.filter(s => s.enrollmentStatus !== SessionExamEnrollmentStatus.NotAdmitted);
    const invalidStatus = gradableStudents?.some(s => !s.status);
    const hasErrors = errors?.some(Boolean);

    const invalidGrade = gradableStudents?.some(s => !s.isRemote && (s.grade === "" || s.grade === null));
    return !hasPermission || invalidStatus || hasErrors || invalidGrade;
  };

  return (
    <div style={{ display: "inline-flex", alignItems: "center", verticalAlign: "middle" }}>
      <Box
        display="flex"
        justifyContent="end"
      >
        <Button
          color="primary"
          size="medium"
          disabled={!checkPermission(getPermissions(), permissions.canPreviewSessionExamStudents)}
          onClick={handleClickOpen}
          startIcon={<ModeEditIcon />}
          label="Оценки"
        />
      </Box>
      <div style={loading ? { opacity: 0.5 } : { opacity: 1 }}>
        <Dialog
          open={open}
          onClose={handleClose}
          fullWidth
          sx={withModalSx({
            "& .MuiDialog-paper": {
              textAlign: "center",
              maxHeight: "80%",
              minWidth: "80%",
              minHeight: "40%",
            },
            "& .MuiDialogActions-root": {
              marginTop: "1rem",
              padding: "1rem",
              justifyContent: "space-between",
            },
          })}
        >
          {loading ? (
            <div style={modalLoading.container}>
              <CircularProgress size={200} />
              <div style={modalLoading.text}>Loading...</div>
            </div>
          ) : (
            <>
              <ModalTitle
                icon={<ModeEditIcon />}
                title="Промяна на статус и оценки"
              />
              <DialogContent
                dividers
                sx={{ px: 10 }}
              >
                <DialogContentText sx={{ pt: 3 }}>
                  <SimpleForm toolbar={false}>
                    {data.length > 0 ? (
                      data.map((student, idx) => {
                        const isAttended = student?.status === StudentExamStatus.Attended;
                        const isNotAdmitted = student?.enrollmentStatus === SessionExamEnrollmentStatus.NotAdmitted;

                        return (
                          <Grid
                            container
                            spacing={2}
                            key={idx}
                            sx={{
                              borderBottom: "1px solid #ddd",
                              ...(isNotAdmitted && {
                                backgroundColor: "#FCECE8",
                                borderLeft: "4px solid #e31212",
                              }),
                            }}
                            mb={2}
                            pb={2}
                          >
                            <Grid
                              item
                              xs={12}
                              md={1}
                              sx={{
                                display: "flex",
                                justifyContent: "start",
                                alignItems: "center",
                              }}
                            >
                              <Typography
                                sx={{
                                  // position: "absolute",
                                  // top: "46%",
                                  // left: "-20px",
                                  width: "30px",
                                  height: "30px",
                                  backgroundColor: "#15023687",
                                  color: "#fff",
                                  borderRadius: "50%",
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                }}
                              >
                                <span>{idx + 1}</span>
                              </Typography>
                            </Grid>

                            {/* Student name */}
                            <Grid
                              item
                              xs={12}
                              md={3}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <Typography
                                color={"black"}
                              >{`${student?.studentFacultyNumber} ${student?.userName}`}</Typography>
                              {isNotAdmitted && (
                                <Chip
                                  label="Недопуснат"
                                  color="error"
                                  size="small"
                                />
                              )}
                              <SecuredComponent permission={permissions.canPreviewStudentRecords}>
                                <Button
                                  label="Студентски картон"
                                  variant="outlined"
                                  size="small"
                                  startIcon={<ReceiptLongIcon />}
                                  onClick={() =>
                                    window.open(
                                      `/${StudentServicePath}${RECORDS}/${STUDENT_EDUCATION}/${student?.studentEducationId}/${DISCIPLINES}/${item.disciplineId}?educationId=${student?.studentEducationId}`,
                                      "_blank",
                                    )
                                  }
                                />
                              </SecuredComponent>
                            </Grid>

                            {/* Status */}
                            <Grid
                              item
                              xs={12}
                              md={3}
                            >
                              <TextField
                                value={student?.status || ""}
                                id="status"
                                variant="filled"
                                label="Статус"
                                select
                                required
                                disabled={isNotAdmitted}
                                error={!isNotAdmitted && !student?.status}
                                helperText={!isNotAdmitted && !student?.status ? "Моля, изберете статус." : ""}
                                onChange={e => handleStatusChange(idx, +e?.target.value, student.isRemote)}
                                sx={{
                                  "& .MuiSelect-select": {
                                    textAlign: "start",
                                  },
                                }}
                              >
                                {StatusExamsChoices.map(choice => (
                                  <MenuItem
                                    key={choice?.id}
                                    value={choice?.id}
                                  >
                                    {choice?.name}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Grid>

                            {/* Grade */}
                            {!student.isRemote ? (
                              <Grid
                                item
                                xs={12}
                                md={2}
                              >
                                <TextField
                                  value={student?.grade || ""}
                                  id="grade"
                                  variant="filled"
                                  label="Оценка"
                                  type="number"
                                  InputProps={{
                                    inputProps: {
                                      max: 6,
                                      min: 2,
                                      onWheel: e => e.target.blur(),
                                    },
                                    disabled: student?.status !== StudentExamStatus.Attended || isNotAdmitted,
                                  }}
                                  error={errors[idx] || false}
                                  helperText={helperTexts[idx] || ""}
                                  onChange={e => {
                                    validateGrade(idx, e?.target.value);
                                    handleChange(idx, "grade", +e?.target.value);
                                  }}
                                />
                              </Grid>
                            ) : (
                              <Grid
                                item
                                xs={12}
                                md={2}
                              >
                                <TextField
                                  value={student?.points || ""}
                                  id="points"
                                  variant="filled"
                                  label="Точки"
                                  type="number"
                                  InputProps={{
                                    inputProps: {
                                      step: 0.01,
                                    },
                                    disabled: isNotAdmitted,
                                  }}
                                  onChange={e => {
                                    handleChange(idx, "points", +e?.target.value);
                                  }}
                                />
                              </Grid>
                            )}
                            <Grid
                              item
                              xs={12}
                              md={3}
                              sx={{ margin: "auto 0" }}
                            >
                              <InputLabel
                                sx={{
                                  width: "100%",
                                  position: "relative",
                                  left: "0px",
                                  top: "-12px",
                                  fontSize: "0.8rem",
                                  color: "rgba(81, 9, 115, 0.69)",
                                  textAlign: "center",
                                }}
                              >
                                Коментар
                                <Typography color={"black"}>{student?.note || "Няма коментар"}</Typography>
                              </InputLabel>
                            </Grid>
                          </Grid>
                        );
                      })
                    ) : (
                      <Typography
                        variant="h6"
                        sx={{ margin: "2rem auto" }}
                      >
                        Няма намерени резултати
                      </Typography>
                    )}
                  </SimpleForm>
                </DialogContentText>
              </DialogContent>

              <DialogActionsButtons
                handleClose={handleClose}
                closeTitle={"Откажи"}
                handleSave={handleSave}
                saveTitle={"Запази"}
                loading={loading}
                saveEnabled={!isSaveDisabled()}
              />
            </>
          )}
        </Dialog>
      </div>
    </div>
  );
};

export default GradesModal;
